/**
 * Utility functions for calculating derived carbon intensity values
 * These values are calculated on-the-fly instead of being stored in the database
 * 
 * IMPORTANT: CROP_CONFIG values must match the backend constant in
 * usacrops-api/src/carbon-intensity/crop-config.constants.ts
 * Update both files when making changes!
 */

// Crop-specific adjustments and fixed values
// Must stay in sync with backend: usacrops-api/src/carbon-intensity/crop-config.constants.ts

// no longer are we using hard wired defaults for ci_score
//now that we have the Dec 23rd 2025 updated spreadsheets, we are using the calculated ci_score from the spreadsheet
/** FD-CIC national avgs (g/bu): Corn 6031→27.4; Sorghum 6444; Soy 7678; Canola 15538 */
export const CROP_CONFIG = {
  'Corn': { adjustment: 0, fixedCiScore: 27.4, fixedGhgPerBu: 6031 },
  'Sorghum': { adjustment: 0, fixedCiScore: 27.5, fixedGhgPerBu: 6444 },
  'Soybean': { adjustment: 0, fixedCiScore: 17.57, fixedGhgPerBu: 7678 },
  'Soybeans': { adjustment: 0, fixedCiScore: 17.57, fixedGhgPerBu: 7678 },
  'Winter Wheat': { adjustment: 0, fixedCiScore: 25.83, fixedGhgPerBu: 11067 },
  'Spring Wheat': { adjustment: 0, fixedCiScore: 22.6, fixedGhgPerBu: 13419 },
  // Canola FD-CIC is g GHG/bu (was g/lb in GREET); CI factor TBD
  'Canola': { adjustment: 0, fixedCiScore: 18.88, fixedGhgPerBu: 15538 },
} as const;

export type CropType = keyof typeof CROP_CONFIG;

/**
 * Conversion factor for CI score to kg GHG/mmBTU
 * Must stay in sync with backend CONVERSION_FACTOR
 */
export const CONVERSION_FACTOR = 1.055056;

/**
 * Calculate all derived carbon intensity values for a cropped field.
 * Uses API/calculated values (default_ci_score, default_total_ghg) when available;
 * falls back to CROP_CONFIG only when API values are missing.
 */
export function calculateDerivedCarbonValues(
  cropType: string,
  ciScore: number | null,
  defaultCiScore: number | null,
  totalGhg: number | null,
  defaultTotalGhg: number | null,
  bushelsPerAcre: number | null
) {
  const cropConfig = CROP_CONFIG[cropType as CropType];

  const adjustedCiScore = ciScore ?? 0;
  const fixedDefaultCiScore = defaultCiScore ?? 0;

  const yieldValue = bushelsPerAcre ?? 0;

  // Prefer API/calculated values; use CROP_CONFIG only as fallback when no API value
  const hasDefaultCi = defaultCiScore != null && Number.isFinite(defaultCiScore);
  const hasDefaultGhg = defaultTotalGhg != null && Number.isFinite(defaultTotalGhg);
  const ghgPerMjFixed = hasDefaultCi ? defaultCiScore! : (cropConfig ? cropConfig.fixedCiScore : null);
  const ghgPerBuFixed = hasDefaultGhg ? defaultTotalGhg! : (cropConfig ? cropConfig.fixedGhgPerBu : null);

  const kgGhgPerMmbtu = adjustedCiScore * CONVERSION_FACTOR;
  const tGhgPerAcreResult = (totalGhg ?? 0) * yieldValue / 1000000;
  const tGhgPerAcreGreetDefault = defaultTotalGhg != null && Number.isFinite(defaultTotalGhg)
    ? (defaultTotalGhg * yieldValue) / 1000000
    : (cropConfig ? (cropConfig.fixedGhgPerBu * yieldValue) / 1000000 : 0);
  const tGhgPerAcreSequestered = tGhgPerAcreGreetDefault - tGhgPerAcreResult;

  return {
    adjustedCiScore,
    fixedDefaultCiScore,
    ghgPerBuFixed,
    ghgPerMjFixed,
    kgGhgPerMmbtu,
    tGhgPerAcreResult,
    tGhgPerAcreGreetDefault,
    tGhgPerAcreSequestered,
  };
}

/**
 * Format a number with specified decimal places
 */
export function formatNumber(value: number | null, decimals: number = 2): string {
  if (value === null || value === undefined) return '';
  return value.toFixed(decimals);
}

/**
 * Get color for sequestered value (green if positive, red if negative)
 */
export function getSequesteredColor(value: number | null): string {
  if (value === null || value === undefined) return '';
  return value > 0 ? '#28a745' : '#dc3545';
}
